import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import api from '../../services/api';

export default function AdminInsights() {
  const [activeSubTab, setActiveSubTab] = useState('articles'); // articles, subscribers

  const [articles, setArticles] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editingArticle, setEditingArticle] = useState(null);
  const [saveToast, setSaveToast] = useState(false);

  useEffect(() => {
    Promise.all([api.getCmsArticles(), api.getNewsletterSubscribers()])
      .then(([articlesRes, subsRes]) => {
        setArticles(articlesRes.articles || []);
        setSubscribers(subsRes.subscribers || []);
      })
      .catch(err => console.error('Error loading insights data:', err))
      .finally(() => setLoading(false));
  }, []);

  const handleSaveArticle = async (e) => {
    e.preventDefault();
    try {
      if (editingArticle.isNew) {
        const res = await api.createCmsArticle(editingArticle);
        setArticles([{ ...editingArticle, id: res.id, views: 0, status: 'Published', published_at: new Date().toISOString().split('T')[0] }, ...articles]);
      } else {
        await api.updateCmsArticle(editingArticle.id, editingArticle);
        setArticles(articles.map(a => a.id === editingArticle.id ? editingArticle : a));
      }
      setEditingArticle(null);
      setSaveToast(true);
      setTimeout(() => setSaveToast(false), 2500);
    } catch (err) {
      alert(err.message || 'Failed to save article.');
    }
  };

  const handleExportCSV = () => {
    window.location.href = api.getNewsletterExportUrl();
  };

  if (loading) {
    return <p className="text-zinc-500 text-sm">Loading insights data...</p>;
  }

  return (
    <div className="space-y-8 font-sans">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-serif font-bold text-white">Insights & Intelligence Briefs</h2>
          <p className="text-xs text-zinc-500 mt-1 font-light">Publish strategic articles and manage subscriber lists.</p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setActiveSubTab('articles')}
            className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors ${activeSubTab === 'articles' ? 'bg-blue-600 text-white' : 'bg-zinc-900 text-zinc-400 hover:text-white'}`}
          >
            Articles ({articles.length})
          </button>
          <button
            onClick={() => setActiveSubTab('subscribers')}
            className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors ${activeSubTab === 'subscribers' ? 'bg-blue-600 text-white' : 'bg-zinc-900 text-zinc-400 hover:text-white'}`}
          >
            Subscribers ({subscribers.length})
          </button>
        </div>
      </div>

      {saveToast && (
        <div className="p-4 bg-emerald-950/30 border border-emerald-500/20 text-emerald-400 text-xs rounded-xl flex items-center gap-2">
          <SafeIcon name="CheckCircle" className="w-4 h-4" />
          Intelligence article updated successfully.
        </div>
      )}

      {/* ARTICLES TAB */}
      {activeSubTab === 'articles' && (
        <div className="space-y-6">
          <div className="flex justify-end">
            <button
              onClick={() => setEditingArticle({ isNew: true, title: '', category: 'Competitor Advertising Signals', excerpt: '', content: '', image_url: '' })}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-2 shadow-lg shadow-blue-600/20"
            >
              <SafeIcon name="Plus" className="w-4 h-4" />
              Write New Article
            </button>
          </div>

          <div className="bg-zinc-950 border border-zinc-900 rounded-3xl overflow-hidden shadow-2xl">
            <table className="w-full text-left text-xs">
              <thead className="bg-zinc-900/50 text-zinc-400 text-[10px] font-bold uppercase tracking-wider border-b border-zinc-900">
                <tr>
                  <th className="p-4 pl-6">Article Title</th>
                  <th className="p-4">Category</th>
                  <th className="p-4">Date Published</th>
                  <th className="p-4">Readership</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 pr-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-900 text-zinc-300">
                {articles.map(art => (
                  <tr key={art.id} className="hover:bg-zinc-900/20 transition-colors">
                    <td className="p-4 pl-6 font-bold text-white max-w-md truncate">{art.title}</td>
                    <td className="p-4">
                      <span className="px-2 py-0.5 rounded bg-zinc-900 text-zinc-400 text-[10px]">
                        {art.category}
                      </span>
                    </td>
                    <td className="p-4 text-zinc-500">{art.published_at}</td>
                    <td className="p-4 text-zinc-400 font-mono">{art.views} views</td>
                    <td className="p-4">
                      <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[9px] font-bold uppercase tracking-widest border border-emerald-500/20">
                        {art.status}
                      </span>
                    </td>
                    <td className="p-4 pr-6 text-right">
                      <button
                        onClick={() => setEditingArticle({ ...art })}
                        className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-colors"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
                {articles.length === 0 && (
                  <tr><td colSpan="6" className="py-16 text-center text-zinc-600 text-xs">No articles yet. Write one to publish it to Insights.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SUBSCRIBERS TAB */}
      {activeSubTab === 'subscribers' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <p className="text-xs text-zinc-400 font-light">Subscribers captured from article lead magnets and newsletter forms.</p>
            <button
              onClick={handleExportCSV}
              className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2"
            >
              <SafeIcon name="Download" className="w-4 h-4 text-blue-400" />
              Export Subscribers CSV
            </button>
          </div>

          <div className="bg-zinc-950 border border-zinc-900 rounded-3xl overflow-hidden shadow-2xl">
            <table className="w-full text-left text-xs">
              <thead className="bg-zinc-900/50 text-zinc-400 text-[10px] font-bold uppercase tracking-wider border-b border-zinc-900">
                <tr>
                  <th className="p-4 pl-6">Subscriber Email</th>
                  <th className="p-4">Signup Source</th>
                  <th className="p-4">Subscription Date</th>
                  <th className="p-4 pr-6 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-900 text-zinc-300">
                {subscribers.map(sub => (
                  <tr key={sub.id} className="hover:bg-zinc-900/20 transition-colors">
                    <td className="p-4 pl-6 font-mono text-white">{sub.email}</td>
                    <td className="p-4 text-zinc-400 font-mono text-[11px]">{sub.source}</td>
                    <td className="p-4 text-zinc-500">{new Date(sub.created_at).toLocaleDateString()}</td>
                    <td className="p-4 pr-6 text-right">
                      <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest border ${sub.status === 'subscribed' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>
                        {sub.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {subscribers.length === 0 && (
                  <tr><td colSpan="4" className="py-16 text-center text-zinc-600 text-xs">No subscribers yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Article Modal */}
      {editingArticle && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-950 border border-zinc-900 rounded-3xl max-w-lg w-full p-8 space-y-6 shadow-2xl">
            <div className="flex justify-between items-center border-b border-zinc-900 pb-4">
              <h3 className="text-lg font-bold text-white">{editingArticle.isNew ? 'Write Intelligence Brief' : 'Edit Brief'}</h3>
              <button onClick={() => setEditingArticle(null)} className="text-zinc-500 hover:text-white">
                <SafeIcon name="X" className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveArticle} className="space-y-4 text-xs">
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Article Title</label>
                <input
                  type="text"
                  className="input-field"
                  value={editingArticle.title}
                  onChange={e => setEditingArticle({ ...editingArticle, title: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Category</label>
                <select
                  className="input-field"
                  value={editingArticle.category}
                  onChange={e => setEditingArticle({ ...editingArticle, category: e.target.value })}
                >
                  <option value="Competitor Advertising Signals">Competitor Advertising Signals</option>
                  <option value="Creative Strategy">Creative Strategy</option>
                  <option value="African Market Intelligence">African Market Intelligence</option>
                  <option value="Brand & Digital Growth">Brand & Digital Growth</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Excerpt</label>
                <textarea
                  rows={2}
                  className="input-field resize-none"
                  placeholder="One-line summary shown on the Insights list page"
                  value={editingArticle.excerpt || ''}
                  onChange={e => setEditingArticle({ ...editingArticle, excerpt: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Article Body</label>
                <textarea
                  rows={6}
                  className="input-field resize-none"
                  placeholder="Full article text shown when a reader opens the brief"
                  value={editingArticle.content || ''}
                  onChange={e => setEditingArticle({ ...editingArticle, content: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Cover Image URL</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="https://... (leave blank to show a placeholder)"
                  value={editingArticle.image_url || ''}
                  onChange={e => setEditingArticle({ ...editingArticle, image_url: e.target.value })}
                />
              </div>

              {!editingArticle.isNew && (
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-zinc-400 uppercase">Status</label>
                  <select
                    className="input-field"
                    value={editingArticle.status}
                    onChange={e => setEditingArticle({ ...editingArticle, status: e.target.value })}
                  >
                    <option value="Published">Published</option>
                    <option value="Draft">Draft</option>
                  </select>
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-zinc-900">
                <button
                  type="button"
                  onClick={() => setEditingArticle(null)}
                  className="w-1/2 bg-zinc-900 text-zinc-400 hover:text-white py-3 rounded-xl font-bold uppercase tracking-wider text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="w-1/2 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-bold uppercase tracking-wider text-xs shadow-lg shadow-blue-600/20"
                >
                  Save Article
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
